import { getLiquidTemplateVariableRegex } from "./sms";

interface TransformFunctions<T> {
  onText: (text: string) => T;
  /** `raw` is the link as written: the bare URL, or the whole `[](url)`. */
  onLink: (url: string, raw: string) => T;
  onDiscount: (discountCode: string) => T;
  onDiscountLink: (discountId: string) => T;
  /** `raw` is the variable as written, braces included. */
  onVariable: (
    variable: string,
    defaultValue: string | undefined,
    raw: string,
  ) => T;
  onNewline: () => T;
}

const TRAILING_SENTENCE_PUNCT = new Set([".", ",", ";", ":", "!", "?"]);

/**
 * Trim trailing punctuation from a candidate URL:
 *
 *   - Trailing .,;:!? is stripped
 *   - A trailing `)` or `]` is kept ONLY if it has a matching opener earlier
 *     in the URL. An unbalanced closer (e.g. URL inside parentheses in the
 *     surrounding text) is stripped.
 *   - Trimming is iterative: `https://x.com/foo.,!` strips all three.
 */
function trimUrlTrailingPunctuation(url: string): string {
  let end = url.length;
  while (end > 0) {
    const last = url.charAt(end - 1);
    if (TRAILING_SENTENCE_PUNCT.has(last)) {
      end--;
      continue;
    }
    if (last === ")" || last === "]") {
      const opener = last === ")" ? "(" : "[";
      let opens = 0;
      let closes = 0;
      for (let i = 0; i < end; i++) {
        const c = url.charAt(i);
        if (c === opener) {
          opens++;
        } else if (c === last) {
          closes++;
        }
      }
      if (closes > opens) {
        end--;
        continue;
      }
    }
    break;
  }
  return url.slice(0, end);
}

/**
 * Generic function to parse SMS text with markdown links, discount codes, variables and newlines
 * to use this async, just have T be a Promise.
 * @param content The SMS text content to parse
 * @param transforms Object containing transform functions for each type of content
 * @returns Array of transformed segments
 */
export function parseAndTransformSmsText<T>(
  content: string,
  transforms: TransformFunctions<T>,
): T[] {
  const segments: T[] = [];
  let lastIndex = 0;
  const variableRegex = getLiquidTemplateVariableRegex();
  const discountVariableRegex = /(%%(?<discountCode>[^%]+)%%)/;
  // Discount link pattern: $$discountId$$ - generates checkout URL with discount auto-applied
  const discountLinkVariableRegex = /(\$\$(?<discountLinkId>[^$]+)\$\$)/;
  const linkRegex = /(\[\]\((?<url>[^)]+)\))/;
  // Plain URL regex: greedy match up to whitespace/brackets/quotes.
  // Trailing punctuation is trimmed post-match by trimUrlTrailingPunctuation
  // so balanced parens inside the URL are preserved.
  const plainUrlRegex = /(?<plainUrl>\b(?:(?:https?|ftp):\/\/)[^\s<>"']+)/i;
  const newlineRegex = /(?<newline>\n)/;
  // Combined regex to match all patterns
  const combinedRegex = new RegExp(
    `${linkRegex.source}|${plainUrlRegex.source}|${discountLinkVariableRegex.source}|${discountVariableRegex.source}|${variableRegex.source}|${newlineRegex.source}`,
    "g",
  );

  let match;

  while ((match = combinedRegex.exec(content)) !== null) {
    // Add text before the match
    if (match.index > lastIndex) {
      segments.push(transforms.onText(content.slice(lastIndex, match.index)));
    }

    const { groups } = match;
    let consumedLength = match[0].length;

    if (groups?.newline) {
      segments.push(transforms.onNewline());
    } else if (groups?.url) {
      //This is a markdown link
      segments.push(transforms.onLink(groups.url, match[0]));
    } else if (groups?.plainUrl) {
      const trimmedUrl = trimUrlTrailingPunctuation(groups.plainUrl);
      segments.push(transforms.onLink(trimmedUrl, trimmedUrl));
      // Stripped chars flow back into the next text segment.
      consumedLength = trimmedUrl.length;
    } else if (groups?.discountLinkId) {
      // Handle discount link pattern $$discountId$$
      segments.push(transforms.onDiscountLink(groups.discountLinkId));
    } else if (groups?.discountCode) {
      segments.push(transforms.onDiscount(groups.discountCode));
    } else if (groups?.variable) {
      segments.push(
        transforms.onVariable(groups.variable.trim(), undefined, match[0]),
      );
    }

    lastIndex = match.index + consumedLength;
    combinedRegex.lastIndex = lastIndex;
  }

  // Add remaining text
  if (lastIndex < content.length) {
    segments.push(transforms.onText(content.slice(lastIndex)));
  }

  return segments;
}

/**
 * Extracts all discount IDs from a template string.
 * Discount IDs are in the format %%discountId%% (code) or $$discountId$$ (link)
 */
export function extractDiscountIdsFromTemplate(content: string): string[] {
  const discountIds: string[] = [];
  parseAndTransformSmsText(content, {
    onText: () => {},
    onLink: () => {},
    onDiscount: (discountId) => {
      discountIds.push(discountId);
    },
    onDiscountLink: (discountId) => {
      discountIds.push(discountId);
    },
    onVariable: () => {},
    onNewline: () => {},
  });
  return discountIds;
}

/**
 * Checks if template content consists only of a discount code variable.
 * Returns the discount ID if the template is only %%discountId%% or $$discountId$$ (with optional whitespace),
 * otherwise returns undefined.
 *
 * This is used to detect when ShopperAI should be triggered with discount capability
 * instead of using the template directly.
 */
export function extractDiscountIdIfOnlyDiscount(
  content: string,
): string | undefined {
  const trimmed = content.trim();
  // Match %%discountId%% or $$discountId$$ patterns
  const discountCodeMatch = trimmed.match(/^%%([^%]+)%%$/);
  const discountLinkMatch = trimmed.match(/^\$\$([^$]+)\$\$$/);

  if (discountCodeMatch) {
    return discountCodeMatch[1];
  }
  if (discountLinkMatch) {
    return discountLinkMatch[1];
  }
  return undefined;
}
